import { useState, useEffect, useRef } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type SortingState,
  type PaginationState,
} from '@tanstack/react-table'
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Star, FolderPlus, Folder } from 'lucide-react'
import ArticleDetail from './ArticleDetail'
import { bookmarkApi, type BookmarkFolder } from '../api/bookmarks'
import { datasetApi, searchApi, type ArticleDetailsResponse } from '../api/search'
import Popover from './Popover'

interface PageFetcherOpts {
  offset: number
  limit: number
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
}

interface ResultsTableProps {
  articles: any[]
  topicMapping?: Record<string, number>
  topicNames?: Record<string, string>
  onBookmarkToggle?: (pmid: string, isBookmarked: boolean) => void
  /** Dataset id when rendering a saved dataset — used to lazy-fetch heavy fields (abstract, affiliations, …). */
  datasetId?: string | null
  /** Search id when rendering live search results — used to lazy-fetch heavy fields. */
  searchId?: string | null
  /** Optional server-side pagination. When set, `articles` is treated as the
   *  current page and the table delegates pagination/sort to the backend. */
  pageFetcher?: {
    fetch: (opts: PageFetcherOpts) => Promise<{ rows: any[]; total: number }>
    /** Cache-busting key — changes when the underlying filter set changes. */
    refreshKey?: string | number
  }
}

export default function ResultsTable({ articles, topicMapping, topicNames, onBookmarkToggle, datasetId, searchId, pageFetcher }: ResultsTableProps) {
  console.log('DEBUG ResultsTable props:', {
    articleCount: articles.length,
    hasTopicMapping: !!topicMapping,
    topicMappingKeys: topicMapping ? Object.keys(topicMapping).length : 0,
    hasTopicNames: !!topicNames,
    topicNamesKeys: topicNames ? Object.keys(topicNames) : [],
    topicNamesSample: topicNames ? Object.entries(topicNames).slice(0, 3) : []
  })
  
  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })

  // Server-side paging: when pageFetcher is provided, ResultsTable owns
  // the current page rows + total count and refetches whenever
  // pagination/sort/refreshKey change.
  const isServerMode = !!pageFetcher
  const [serverRows, setServerRows] = useState<any[]>([])
  const [serverTotal, setServerTotal] = useState<number>(0)
  const [serverLoading, setServerLoading] = useState(false)

  useEffect(() => {
    if (!pageFetcher) return
    let cancelled = false
    setServerLoading(true)
    const sortBy = sorting[0]?.id
    const sortDir = sorting[0]?.desc ? 'desc' : 'asc'
    pageFetcher
      .fetch({
        offset: pagination.pageIndex * pagination.pageSize,
        limit: pagination.pageSize,
        sort_by: sortBy,
        sort_dir: sortDir as 'asc' | 'desc',
      })
      .then(res => {
        if (cancelled) return
        setServerRows(res.rows || [])
        setServerTotal(res.total || 0)
      })
      .catch(err => { if (!cancelled) console.error('Server-side page fetch failed:', err) })
      .finally(() => { if (!cancelled) setServerLoading(false) })
    return () => { cancelled = true }
  }, [pageFetcher?.refreshKey, pagination.pageIndex, pagination.pageSize, sorting])

  // When the refreshKey changes (e.g. a new filter applied), reset to first page.
  useEffect(() => {
    if (!pageFetcher) return
    setPagination(p => ({ ...p, pageIndex: 0 }))
  }, [pageFetcher?.refreshKey])

  const [detailsByPmid, setDetailsByPmid] = useState<Record<string, ArticleDetailsResponse>>({})
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set())
  const [bookmarks, setBookmarks] = useState<Set<string>>(new Set())
  const [bookmarkLoading, setBookmarkLoading] = useState<Set<string>>(new Set())
  const [folders, setFolders] = useState<BookmarkFolder[]>([])
  const [popoverPmid, setPopoverPmid] = useState<string | null>(null)
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)
  const [newFolderMode, setNewFolderMode] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const starRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  useEffect(() => {
    const load = async () => {
      try {
        const [bookmarkList, folderList] = await Promise.all([
          bookmarkApi.listBookmarks(),
          bookmarkApi.listFolders(),
        ])
        setBookmarks(new Set(bookmarkList.map(b => b.pmid)))
        setFolders(folderList)
      } catch (err) {
        console.error('Failed to load bookmarks/folders:', err)
      }
    }
    load()
  }, [])

  const refreshFolders = async () => {
    try {
      const folderList = await bookmarkApi.listFolders()
      setFolders(folderList)
    } catch (err) {
      console.error('Failed to refresh folders:', err)
    }
  }

  const removeBookmark = async (pmid: string) => {
    if (bookmarkLoading.has(pmid)) return
    setBookmarkLoading(prev => new Set(prev).add(pmid))
    try {
      await bookmarkApi.removeBookmark(pmid)
      setBookmarks(prev => { const next = new Set(prev); next.delete(pmid); return next })
      onBookmarkToggle?.(pmid, false)
    } catch (err: any) {
      console.error('Failed to remove bookmark:', err)
      alert(err?.response?.data?.detail || 'Failed to remove bookmark')
    } finally {
      setBookmarkLoading(prev => { const next = new Set(prev); next.delete(pmid); return next })
    }
  }

  const addBookmarkToFolder = async (pmid: string, folderId: string | null) => {
    if (bookmarkLoading.has(pmid)) return
    setBookmarkLoading(prev => new Set(prev).add(pmid))
    try {
      await bookmarkApi.addBookmark(pmid, folderId)
      setBookmarks(prev => new Set(prev).add(pmid))
      onBookmarkToggle?.(pmid, true)
      closePopover()
    } catch (err: any) {
      console.error('Failed to add bookmark:', err)
      alert(err?.response?.data?.detail || 'Failed to add bookmark')
    } finally {
      setBookmarkLoading(prev => { const next = new Set(prev); next.delete(pmid); return next })
    }
  }

  const closePopover = () => {
    setPopoverPmid(null)
    setPopoverAnchor(null)
    setNewFolderMode(false)
    setNewFolderName('')
  }

  const createFolderAndBookmark = async (pmid: string) => {
    const name = newFolderName.trim()
    if (!name) return
    try {
      const folder = await bookmarkApi.createFolder({ name })
      setFolders(prev => [...prev, folder])
      setNewFolderMode(false)
      setNewFolderName('')
      await addBookmarkToFolder(pmid, folder.folder_id)
      await refreshFolders()
    } catch (err: any) {
      console.error('Failed to create folder:', err)
      alert(err?.response?.data?.detail || 'Failed to create folder')
    }
  }

  const loadDetailsIfNeeded = async (pmid: string, original: any) => {
    if (detailsByPmid[pmid]) return               // already fetched
    if (original?.abstract != null) return        // row already carries the heavy fields
    if (loadingDetails.has(pmid)) return
    if (!datasetId && !searchId) return           // no context to fetch from
    setLoadingDetails(prev => new Set(prev).add(pmid))
    try {
      const details = datasetId
        ? await datasetApi.getArticleDetails(datasetId, pmid)
        : await searchApi.getArticleDetails(searchId!, pmid)
      setDetailsByPmid(prev => ({ ...prev, [pmid]: details }))
    } catch (err) {
      console.error('Failed to load article details:', err)
    } finally {
      setLoadingDetails(prev => { const next = new Set(prev); next.delete(pmid); return next })
    }
  }

  const handleStarClick = (pmid: string, anchor: HTMLElement) => {
    if (bookmarks.has(pmid)) {
      removeBookmark(pmid)
    } else {
      setPopoverPmid(pmid)
      setPopoverAnchor(anchor)
      setNewFolderMode(false)
      setNewFolderName('')
    }
  }

  const columns = [
    {
      id: 'bookmark',
      header: '',
      size: 40,
      maxSize: 40,
      cell: ({ row }: any) => {
        const pmid = row.original.pmid
        const isBookmarked = bookmarks.has(pmid)
        const isLoading = bookmarkLoading.has(pmid)

        return (
          <button
            ref={(el) => { starRefs.current[pmid] = el }}
            onClick={(e) => {
              e.stopPropagation()
              const anchor = e.currentTarget as HTMLElement
              handleStarClick(pmid, anchor)
            }}
            disabled={isLoading}
            className={`p-1 rounded transition-colors ${
              isBookmarked
                ? 'text-yellow-500 hover:text-yellow-600'
                : 'text-gray-300 hover:text-yellow-400'
            } ${isLoading ? 'opacity-50' : ''}`}
            title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            <Star
              className="w-4 h-4"
              fill={isBookmarked ? 'currentColor' : 'none'}
            />
          </button>
        )
      },
    },
    {
      id: 'expand',
      header: '',
      size: 40,
      maxSize: 40,
      cell: ({ row }: any) => (
        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (!row.getIsExpanded()) {
                              loadDetailsIfNeeded(row.original.pmid, row.original)
                            }
                            row.toggleExpanded()
                          }}
          className="p-1 hover:bg-gray-200 rounded"
        >
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${
              row.getIsExpanded() ? 'rotate-90' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      ),
    },
    {
      accessorKey: 'pmid',
      header: 'PMID',
      size: 80,
      maxSize: 80,
      cell: ({ row }: any) => (
        <a
          href={`https://pubmed.ncbi.nlm.nih.gov/${row.original.pmid}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-600 hover:underline font-mono text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          {row.original.pmid}
        </a>
      ),
    },
    {
      accessorKey: 'topic',
      header: 'Topic',
      size: 100,
      maxSize: 120,
      cell: ({ row }: any) => {
        const pmid = row.original.pmid
        const topicId = topicMapping?.[pmid]
        if (topicId == null) return null
        const topicColors = [
          '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
          '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b',
        ]
        const numericTopicId = Number(topicId)
        const displayNum = String(numericTopicId + 1)
        const color = topicColors[Math.abs(numericTopicId) % topicColors.length]
        const name = topicNames?.[displayNum]
        if (row.index < 3) {
          console.log('DEBUG topic cell:', { pmid, topicId, numericTopicId, displayNum, name, hasTopicNames: !!topicNames, topicNamesKeys: topicNames ? Object.keys(topicNames) : [] })
        }
        return (
          <span
            className="inline-flex items-start px-1.5 py-0.5 rounded text-[10px] font-semibold text-white leading-tight max-w-[120px] break-words whitespace-normal"
            style={{ backgroundColor: color }}
            title={name ? `Topic ${displayNum} - ${name}` : `Topic ${displayNum}`}
          >
            {name ? `${displayNum} - ${name}` : `T${displayNum}`}
          </span>
        )
      },
    },
    {
      accessorKey: 'title',
      header: 'Title',
      size: 380,
      minSize: 200,
      cell: ({ row }: any) => (
        <span className="text-sm line-clamp-2" title={row.original.title}>
          {row.original.title || 'N/A'}
        </span>
      ),
    },
    {
      accessorKey: 'authors',
      header: 'Authors',
      size: 80,
      maxSize: 80,
      cell: ({ row }: any) => (
        <span className="text-sm line-clamp-2 break-words" title={row.original.authors?.join(', ')}>
          {row.original.authors?.join(', ') || 'N/A'}
        </span>
      ),
    },
    {
      accessorKey: 'journal',
      header: 'Journal',
      size: 70,
      maxSize: 70,
      cell: ({ row }: any) => (
        <span className="text-sm line-clamp-2 break-words" title={row.original.journal}>
          {row.original.journal || 'N/A'}
        </span>
      ),
    },
    {
      accessorKey: 'year',
      header: 'Year',
      size: 60,
      maxSize: 60,
      cell: ({ row }: any) => <span className="text-sm">{row.original.year || 'N/A'}</span>,
    },
    {
      accessorKey: 'quartile',
      header: 'Q',
      size: 50,
      maxSize: 50,
      cell: ({ row }: any) => {
        const q = row.original.quartile
        const colors: Record<string, string> = {
          Q1: 'bg-emerald-100 text-emerald-800 border-emerald-200',
          Q2: 'bg-blue-100 text-blue-800 border-blue-200',
          Q3: 'bg-amber-100 text-amber-800 border-amber-200',
          Q4: 'bg-red-100 text-red-800 border-red-200',
        }
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${colors[q] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
            {q || 'N/A'}
          </span>
        )
      },
    },
    {
      accessorKey: 'sjr_score',
      header: 'SJR',
      size: 60,
      maxSize: 60,
      cell: ({ row }: any) => (
        <span className="text-sm font-medium">{row.original.sjr_score?.toFixed(2) || 'N/A'}</span>
      ),
    },
    {
      accessorKey: 'citations',
      header: 'Cites',
      size: 60,
      maxSize: 60,
      cell: ({ row }: any) => {
        const cites = row.original.citations
        const datasetCites = row.original.dataset_citations
        if (datasetCites != null) {
          return (
            <div className="flex flex-col">
              <span className="text-sm font-medium text-brand-700">{datasetCites}</span>
              <span className="text-[10px] text-gray-400">in dataset</span>
            </div>
          )
        }
        return (
          <span className={`text-sm font-medium ${cites != null ? 'text-brand-700' : ''}`}>
            {cites ?? 'N/A'}
          </span>
        )
      },
    },
    {
      accessorKey: 'rcr',
      header: 'RCR',
      size: 60,
      maxSize: 60,
      cell: ({ row }: any) => (
        <span className="text-sm">{row.original.rcr?.toFixed(2) || 'N/A'}</span>
      ),
    },
  ]

  const tableData = isServerMode ? serverRows : articles
  const pageCount = isServerMode
    ? Math.max(1, Math.ceil(serverTotal / pagination.pageSize))
    : undefined

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    // In server mode, the backend already sorts + pages; skip client-side models.
    getSortedRowModel: isServerMode ? undefined : getSortedRowModel(),
    getPaginationRowModel: isServerMode ? undefined : getPaginationRowModel(),
    manualPagination: isServerMode,
    manualSorting: isServerMode,
    pageCount: isServerMode ? pageCount : undefined,
  })

  return (
    <div className="space-y-3">
      <Popover
        anchor={popoverAnchor}
        open={popoverPmid !== null}
        onClose={closePopover}
        align="left"
        width={240}
      >
        {popoverPmid && (
          <div className="text-sm">
            <div className="text-xs text-gray-500 px-2 pt-1 pb-2">Save to folder</div>
            <div className="max-h-56 overflow-y-auto">
              {folders.map(f => (
                <button
                  key={f.folder_id}
                  onClick={() => addBookmarkToFolder(popoverPmid, f.folder_id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left"
                >
                  <Folder className="w-3.5 h-3.5 text-gray-500" />
                  <span className="flex-1 truncate">{f.name}</span>
                  {typeof f.bookmark_count === 'number' && (
                    <span className="text-[10px] text-gray-400">{f.bookmark_count}</span>
                  )}
                </button>
              ))}
              <button
                onClick={() => addBookmarkToFolder(popoverPmid, null)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left"
              >
                <Folder className="w-3.5 h-3.5 text-gray-300" />
                <span className="flex-1 truncate italic text-gray-600">Unsorted</span>
              </button>
            </div>
            <div className="border-t mt-1 pt-1">
              {newFolderMode ? (
                <div className="px-2 py-1.5 flex gap-1">
                  <input
                    autoFocus
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') createFolderAndBookmark(popoverPmid)
                      else if (e.key === 'Escape') { setNewFolderMode(false); setNewFolderName('') }
                    }}
                    placeholder="Folder name…"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                  />
                  <button
                    onClick={() => createFolderAndBookmark(popoverPmid)}
                    disabled={!newFolderName.trim()}
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setNewFolderMode(true)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left text-blue-700"
                >
                  <FolderPlus className="w-3.5 h-3.5" />
                  <span>New folder…</span>
                </button>
              )}
            </div>
          </div>
        )}
      </Popover>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full" style={{ tableLayout: 'fixed' }}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-gray-50 border-b">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                      style={{ width: header.getSize() }}
                    >
                      {header.column.getCanSort() ? (
                        <button
                          className="flex items-center gap-1 hover:text-gray-900 transition-colors group"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <span className="text-gray-400 group-hover:text-gray-600">
                            {header.column.getIsSorted() === 'asc' ? (
                              <ArrowUp className="w-3 h-3" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <ArrowDown className="w-3 h-3" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3" />
                            )}
                          </span>
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-100">
              {table.getRowModel().rows.map((row) => (
                <>
                  <tr
                    key={row.id}
                    className="hover:bg-blue-50/50 transition-colors"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 py-2 border-b border-gray-50"
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr>
                      <td colSpan={columns.length} className="px-0 py-0 border-b">
                        <ArticleDetail
                          article={{ ...row.original, ...(detailsByPmid[row.original.pmid] || {}) }}
                          isLoadingDetails={loadingDetails.has(row.original.pmid)}
                        />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>
            Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{' '}
            <strong>{table.getPageCount()}</strong>
          </span>
          <span className="text-gray-400">| </span>
          <span>{(isServerMode ? serverTotal : articles.length).toLocaleString()} total articles{isServerMode && serverLoading ? ' · loading…' : ''}</span>
        </div>

        <div className="flex items-center gap-1">
          <button
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            title="First page"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          <button
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            title="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-1 mx-2">
            {Array.from({ length: Math.min(table.getPageCount(), 5) }, (_, i) => {
              const pageIndex = i
              return (
                <button
                  key={i}
                  onClick={() => table.setPageIndex(pageIndex)}
                  className={`w-7 h-7 rounded text-xs font-medium ${
                    table.getState().pagination.pageIndex === pageIndex
                      ? 'bg-brand-600 text-white'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  {pageIndex + 1}
                </button>
              )
            })}
            {table.getPageCount() > 5 && (
              <span className="text-gray-400 px-1">... </span>
            )}
          </div>

          <button
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            title="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            title="Last page"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
